import React, { useEffect, useState, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import axiosConn from 'config/axiosConn';

const themes = [
  'Adopt Healthy Lifestyles',
  'Adopt sustainable food systems',
  'Reduce E-Waste',
  'Reduce Waste',
  'Save Energy',
  'Save Water',
  'Say No to Single Use Plastic',
];

const ApexChartHeatmap = (props) => {
  const [chartOptions, setChartOptions] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [state, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);

  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  useEffect(() => {
    const fetchTop10Districts = async (props) => {
      const currentState = props;
      try {
        const response = await fetch('https://ecoclubs.education.gov.in/api/top10-DistrictEvents', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ state: currentState }),
        });

        const data = await response.json();
        const stateNames = data[0] ? [data[0].state] : [];
        setStates(stateNames[0]);
        const districtNames = data.map((item) => item.district);
        setDistricts(districtNames);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
    }
    };

    fetchTop10Districts(props.state);
  }, [props.state]);

  useEffect(() => {
    if (districts.length > 0) {
      const fetchEventData = async () => {
        try {
          const response = await axiosConn('post', 'heat-map', { state });

          if (response && response.data) {
            const allThemes = [];

            response.data.forEach(districtData => {
              const { district, themes } = districtData;

              themes.forEach(themeData => {
                const { theme_name, eventCount } = themeData;

                allThemes.push({ district, theme_name, eventCount });
              });
            });

            const newSeries = themes.map((theme) => ({
              name: theme,
              data: districts.map((district) => {
                const districtData = allThemes.filter(item => item.district === district && item.theme_name === theme);
                return !isNaN(districtData[0]?.eventCount) ? districtData[0].eventCount : 0;
              }),
            }));

            const allEventCounts = allThemes.map(item => item.eventCount);
            const min = Math.min(...allEventCounts);
            const max = Math.max(...allEventCounts);

            setMinValue(min);
            setMaxValue(max);

            const initialChartOptions = {
              series: newSeries,
              options: {
                chart: {
                  height: 350,
                  type: 'heatmap',
                  events: {
                    dataPointSelection: async function (event, chartContext, config) {
                      const selectedDistrict = config.w.config.xaxis.categories[config.dataPointIndex];
                      const selectedTheme = config.w.config.series[config.seriesIndex].name;
                      const data = await fetchData(selectedDistrict, selectedTheme);
                      setSelectedData(data);  // Ensure that the selected data is set here.
                    },
                  },
                },
                plotOptions: {
                  heatmap: {
                    shadeIntensity: 0.1,
                    radius: 0,
                    colorScale: {
                      ranges: [
                        { from: min, to: min + (max - min) * 0.2, name: 'Low', color: '#FFB300' },
                        { from: min + (max - min) * 0.2, to: min + (max - min) * 0.4, name: 'Medium-Low', color: '#FF9800' },
                        { from: min + (max - min) * 0.4, to: min + (max - min) * 0.6, name: 'Medium', color: '#FF4500' },
                        { from: min + (max - min) * 0.6, to: max, name: 'High', color: '#D32F2F' },
                      ],
                    },
                  },
                },
                dataLabels: { enabled: true },
                stroke: { width: 1 },
                title: {
                  text: 'Top 10 districts vs Eco Clubs for Mission LiFE theme',
                  align: 'left',
                  offsetX: 20,
                  style: { fontSize: '20px', fontWeight: 'bold' },
                },
                tooltip: { enabled: false },
                xaxis: {
                  type: 'category',
                  categories: districts,
                  title: { text: 'Top 10 Districts' },
                  labels: { style: { fontSize: '10px' } },
                },
                yaxis: {
                  title: { text: 'Themes' },
                  categories: themes,
                  labels: {
                    style: { fontSize: '10px' },
                    offsetX: -1,
                    formatter: (value) => value.length > 20 ? value.slice(0, 20) + '...' : value,
                  },
                },
              },
            };

            setChartOptions(initialChartOptions);
          }
        } catch (error) {
          console.error('Error fetching event data:', error);
        }
      };

      fetchEventData();
    }
  }, [districts, minValue, maxValue]);

  const fetchData = async (district, theme_name) => {
    try {
      const response = await axiosConn('post', 'top10-SchoolEventsByDistrictAndTheme', { district, theme_name });
      if (response.status === 200) {
        const topSchools = response.data.topSchools.map(item => ({
          name: item.school_name,
          value: item.eventCount,
          district,
        }));

        return {
          state: district,
          schools: topSchools,
          theme: theme_name,
        };
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }

    return {
      state: district,
      districts: [],
      schools: [],
      theme: theme_name,
    };
  };

  const resetSelection = () => {
    console.log('Chart options:', chartOptions);
    if (chartRef.current || chartOptions) {
        console.log('resetSelection called');
        setSelectedData(null);
    }
};

  return (
    <div style={{ position: 'relative', marginBottom: '22px' }}>
      {chartOptions && (
        <div id="chart">
          <ReactApexChart
            ref={chartRef}
            options={chartOptions.options}
            series={chartOptions.series}
            type="heatmap"
            height={350}
          />
        </div>
      )}

{selectedData?.schools && selectedData.schools.length > 0 && !loading && (
  <div style={{ marginTop: '20px' }}>
    <h4 style={{ marginTop: '20px', fontWeight: 'bold' }}>Top Schools</h4>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>School</th>
          <th style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>Event Count</th>
        </tr>
      </thead>
      <tbody>
        {console.log("Selected Schools Data:", selectedData.schools)}
        {selectedData.schools.map((school, index) => (
          <tr key={index}>
            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{school.name}</td>
            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{school.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}


      <button
        className="resetButtonbtn"
        onClick={resetSelection}
        style={{
          position: 'absolute',
          top: '1px',
          right: '50px',
          padding: '5px 10px',
          cursor: 'pointer',
          fontSize: '12px',
          backgroundColor: '#f44336',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
        }}
      >
        Reset
      </button>
    </div>
  );
};

export default ApexChartHeatmap;
